import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api, { formatApiError } from "@/lib/api";
import { COUNTRIES, getStatesFor } from "@/data/locations";
import { todayIn } from "@/lib/dates";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Plus, Minus, Trash2, Pencil, Wallet, Link2, X, Users, Network, ChevronDown, ChevronsDownUp, ChevronsUpDown, Maximize2, RotateCcw, Move, Download, FileSpreadsheet, FileText, UserPlus, KeyRound, Copy, Check, ShieldCheck, ShieldOff, Expand, Shrink, Eye, MapPin, Search, Upload, AlertCircle, History, RefreshCw, FolderSync, Database, Printer, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useCaPrice from "@/lib/useCaPrice";
import CaryPactLiveSyncModal from "@/components/CaryPactLiveSyncModal";
import CaryPactHistoryModal from "@/components/CaryPactHistoryModal";
import { vipInfo, fmtUsdShort } from "@/lib/carypactVip";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EMPTY = { name: "", parent_id: "", wallet_name: "", amount: "", current_value: "", rank: "", invite_link: "", email: "", country: "", state: "", wallet_kind: "", forecast_date: "", status: "", affiliate_name: "" };

// Pretty-print a raw CaryPact field value for the tile's "CP data" panel.
const fmtCpVal = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  const s = String(v);
  const n = Number(s);
  if (s.trim() !== "" && isFinite(n) && !s.startsWith("0x")) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return s.length > 22 ? `${s.slice(0, 10)}…${s.slice(-6)}` : s;
};
const RANK_OPTIONS = ["V1", "V2", "V3", "V4", "V5", "V6", "V7", "V8", "V9", "V10", "V11"];

export default function OrgChart() {
  const { user, startImpersonation } = useAuth();
  const { price: caPriceLive } = useCaPrice();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusParam = searchParams.get("focus");
  const focusConsumedRef = useRef(false);
  const [imperBusy, setImperBusy] = useState(null);
  const [syncNode, setSyncNode] = useState(null);
  const [chartRootSync, setChartRootSync] = useState(false);
  const chart = "carypact_live"; // sync-only chart — manual "My Org" retired (data kept in DB)
  const [posterBusy, setPosterBusy] = useState(false);
  const isLive = chart === "carypact_live";
  const isAdmin = user?.role === "admin";
  const [allNodes, setAllNodes] = useState([]);
  // Tiles named "UNUSED" (and everything under them) are hidden from the chart and all counts.
  const nodes = useMemo(() => {
    const isUnused = (n) => (n.name || "").trim().toUpperCase() === "UNUSED";
    if (!allNodes.some(isUnused)) return allNodes;
    const kids = new Map();
    allNodes.forEach((n) => {
      const k = n.parent_id || null;
      if (!kids.has(k)) kids.set(k, []);
      kids.get(k).push(n);
    });
    const hidden = new Set();
    const mark = (id) => {
      if (hidden.has(id)) return;
      hidden.add(id);
      (kids.get(id) || []).forEach((c) => mark(c.id));
    };
    allNodes.filter(isUnused).forEach((n) => mark(n.id));
    return allNodes.filter((n) => !hidden.has(n.id));
  }, [allNodes]);
  const [revenueByUser, setRevenueByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [grant, setGrant] = useState({ role: "participant", method: "invite", loading: false, result: null, error: "" });
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");

  const [chartOpen, setChartOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const collapsedInitRef = useRef(false);
  const [focusedId, setFocusedId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);

  // Close the search dropdown on outside click or Escape.
  useEffect(() => {
    if (!searchOpen) return;
    const onDown = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setSearchOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [searchOpen]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("skip"); // "skip" | "update"
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // "detailed" = full tiles (money, chips, actions) · "simple" = just names + tree lines.
  const [cpOpen, setCpOpen] = useState(() => new Set());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [leaderNoteHidden, setLeaderNoteHidden] = useState(() => localStorage.getItem("leader_scope_note_hidden") === "1");
  const toggleCpOpen = (id) => setCpOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("org_view_mode") || "detailed"; } catch { return "detailed"; }
  });
  const setMode = (m) => {
    setViewMode(m);
    try { localStorage.setItem("org_view_mode", m); } catch { /* private mode */ }
  };
  const dragRef = useRef(null);
  const draggedRef = useRef(false);
  const viewportRef = useRef(null);
  const treeRef = useRef(null);

  const fetchNodes = async () => {
    try {
      const { data } = await api.get(`/org-nodes?chart=${chart}`);
      setAllNodes(data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  // Toolbar refresh — refetch nodes AND revenue rollups with clear feedback.
  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const [n, r] = await Promise.all([
        api.get(`/org-nodes?chart=${chart}`),
        api.get("/admin/org-revenue-rollup").catch(() => ({ data: {} })),
      ]);
      setAllNodes(n.data);
      setRevenueByUser(r.data || {});
      toast.success(`Org chart refreshed · ${n.data.length} wallets`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    collapsedInitRef.current = false;
    (async () => {
      try {
        const [n, r] = await Promise.all([
          api.get(`/org-nodes?chart=${chart}`),
          api.get("/admin/org-revenue-rollup").catch(() => ({ data: {} })),
        ]);
        setAllNodes(n.data);
        setRevenueByUser(r.data || {});
      } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [chart, user?.id]);

  // On first load, collapse every parent so the tree shows just the root tiles.
  // Subsequent re-fetches don't re-collapse — the user's expand/collapse choices stick.
  // When ?focus=<nodeId> is present (deep link from the CRM), keep that node's
  // ancestor chain expanded so the tile is visible and focusable.
  useEffect(() => {
    if (collapsedInitRef.current || nodes.length === 0) return;
    const parents = new Set(nodes.map((n) => n.parent_id).filter(Boolean));
    // Default view for EVERYONE = one row of downline: roots stay expanded,
    // deeper levels start collapsed. A leader's own top tile counts as a root even
    // though its parent (their upline) is out of scope and not in this list.
    const visible = new Set(nodes.map((n) => n.id));
    nodes.filter((n) => !n.parent_id || !visible.has(n.parent_id)).forEach((n) => parents.delete(n.id));
    if (focusParam) {
      const byId = new Map(nodes.map((n) => [n.id, n]));
      let cur = byId.get(focusParam);
      while (cur && cur.parent_id) { parents.delete(cur.parent_id); cur = byId.get(cur.parent_id); }
    }
    setCollapsed(parents);
    collapsedInitRef.current = true;
  }, [nodes]);

  // Deep-link focus: once the tree is rendered and fit() has settled, zoom into
  // the requested tile. One-shot — later refetches don't re-trigger it.
  useEffect(() => {
    if (!focusParam || focusConsumedRef.current || loading || nodes.length === 0) return;
    if (!nodes.some((n) => n.id === focusParam)) return;
    const t = setTimeout(() => {
      focusConsumedRef.current = true;
      focusNodeRef.current?.(focusParam);
    }, 650);
    return () => clearTimeout(t);
  }, [nodes, loading, focusParam]);

  const soOf = (n) => (typeof n.sort_order === "number" ? n.sort_order : 1e15);
  const childrenOf = (id) => nodes
    .filter((n) => (n.parent_id || null) === (id || null))
    .sort((a, b) => soOf(a) - soOf(b));
  const visibleIds = new Set(nodes.map((n) => n.id));
  const roots = nodes.filter((n) => !n.parent_id || !visibleIds.has(n.parent_id));
  // Totals — principal (starting) vs current value (live worth). When a tile has
  // no `current_value` we fall back to its principal so a mixed tree still totals
  // sensibly. Delta % shows portfolio performance at a glance.
  const totalPrincipal = nodes.reduce((a, n) => a + (n.amount || 0), 0);
  const totalCurrent   = nodes.reduce((a, n) => a + (n.current_value != null ? Number(n.current_value) : (n.amount || 0)), 0);
  const totalAmount    = totalCurrent; // used elsewhere (PDF footer) — keep alias
  const totalDeltaPct  = totalPrincipal > 0 ? ((totalCurrent - totalPrincipal) / totalPrincipal) * 100 : 0;
  // Community KPI feeds — straight from the top wallet's own synced CaryPact data.
  const rootCp = roots[0]?.cp_data || {};
  const directRefs = roots.length ? childrenOf(roots[0].id).length : 0;

  // Search: compute the set of node ids that match the query (case-insensitive across
  // name / wallet_name / email / country / state) and auto-expand every ancestor so
  // each match is rendered. Empty search → null = no filtering.
  const searchInfo = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return { matches: null, expandedByMatch: new Set() };
    const matches = new Set();
    for (const n of nodes) {
      const hay = [n.name, n.wallet_name, n.email, n.country, n.state]
        .filter(Boolean).join(" ").toLowerCase();
      if (hay.includes(q)) matches.add(n.id);
    }
    // walk up each match's parent chain so the match is visible after collapse
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const expanded = new Set();
    for (const id of matches) {
      let cur = byId.get(id);
      while (cur && cur.parent_id) {
        expanded.add(cur.parent_id);
        cur = byId.get(cur.parent_id);
      }
    }
    return { matches, expandedByMatch: expanded };
  })();
  const searchInfoRef = useRef(searchInfo);
  searchInfoRef.current = searchInfo;
  const effectiveCollapsed = (id) =>
    collapsed.has(id) && !searchInfo.expandedByMatch.has(id);

  // depth-first flatten with level, used for downloads
  const flatten = () => {
    const out = [];
    const walk = (node, level) => {
      out.push({ node, level });
      childrenOf(node.id).forEach((c) => walk(c, level + 1));
    };
    roots.forEach((r) => walk(r, 0));
    return out;
  };
  const maxDepth = flatten().reduce((m, r) => Math.max(m, r.level), 0);

  // Pre-compute downline rollups per node: total dollars (own amount + all descendants),
  // total people count (excluding the node itself), and per-stream revenue rollups
  // (Mining / Staking / Community in USD — node's own + all descendants).
  // One post-order walk → O(n).
  const downline = useMemo(() => {
    const kidsByParent = new Map();
    nodes.forEach((n) => {
      const k = n.parent_id || null;
      if (!kidsByParent.has(k)) kidsByParent.set(k, []);
      kidsByParent.get(k).push(n);
    });
    const totals = {};
    const counts = {};
    const mining = {};
    const staking = {};
    const community = {};
    const ownRev = (uid) => revenueByUser[uid] || { mining_usd: 0, staking_usd: 0, community_usd: 0 };
    const walk = (n) => {
      const kids = kidsByParent.get(n.id) || [];
      let total = n.amount || 0;
      let count = 0;
      const r = ownRev(n.user_id);
      let mUsd = r.mining_usd || 0;
      let sUsd = r.staking_usd || 0;
      let cUsd = r.community_usd || 0;
      for (const k of kids) {
        const rr = walk(k);
        total += rr.total;
        count += 1 + rr.count;
        mUsd += rr.mUsd;
        sUsd += rr.sUsd;
        cUsd += rr.cUsd;
      }
      totals[n.id] = total;
      counts[n.id] = count;
      mining[n.id] = mUsd;
      staking[n.id] = sUsd;
      community[n.id] = cUsd;
      return { total, count, mUsd, sUsd, cUsd };
    };
    // A scoped view (a leader sees their own branch, never the upline above it) has
    // NO parent_id === null tile — its top tiles point at an upline they can't see.
    // Walk from every tile whose parent isn't in view, or the whole branch rolls up
    // to nothing and their tiles lose the Downline line Stuart's tiles have.
    const visible = new Set(nodes.map((n) => n.id));
    nodes.filter((n) => !n.parent_id || !visible.has(n.parent_id)).forEach(walk);
    return { totals, counts, mining, staking, community };
  }, [nodes, revenueByUser]);

  // Compact USD formatter — keeps tiles tidy ($12.5k, $1.2M).
  const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 10e6 ? 0 : 1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(n >= 10e3 ? 0 : 1)}k`;
    return `$${Math.round(n).toLocaleString()}`;
  };

  // ----- zoom & pan -----
  const fit = () => {
    const vp = viewportRef.current, tr = treeRef.current;
    if (!vp || !tr) return;
    const w = tr.scrollWidth || 1, h = tr.scrollHeight || 1;
    const z = Math.min((vp.clientWidth - 56) / w, (vp.clientHeight - 56) / h, 1.4);
    setZoom(Math.max(0.05, +z.toFixed(2)));
    setPan({ x: 0, y: 0 });
  };
  const zoomIn = () => applyZoom(zoom + 0.1);
  const zoomOut = () => applyZoom(zoom > 0.15 ? zoom - 0.1 : zoom - 0.05);
  const reset = () => { setZoom(1); setPan({ x: 0, y: 0 }); setFocusedId(null); };

  // Center the viewport on a tile at a higher zoom — "click to focus".
  // Inverts the current transform to find the tile's tree-local coords, then sets
  // pan/zoom so the tile lands at the viewport center.
  const centerOnNode = (nodeId, newZ) => {
    const vp = viewportRef.current;
    if (!vp) return false;
    const tile = vp.querySelector(`[data-node-id="${nodeId}"]`);
    if (!tile) return false;
    const vpRect = vp.getBoundingClientRect();
    const tileRect = tile.getBoundingClientRect();
    const tcxNow = tileRect.left + tileRect.width / 2 - vpRect.left;
    const tcyNow = tileRect.top + tileRect.height / 2 - vpRect.top;
    // tree origin in viewport coords (see render: left:50% top:28 + translate(-50%+panX, panY) scale(zoom))
    const tx = (tcxNow - vp.clientWidth / 2 - pan.x) / zoom;
    const ty = (tcyNow - 28 - pan.y) / zoom;
    setZoom(newZ);
    setPan({ x: -tx * newZ, y: vp.clientHeight / 2 - 28 - ty * newZ });
    return true;
  };

  const focusNode = (nodeId) => {
    if (centerOnNode(nodeId, 1.6)) setFocusedId(nodeId);
  };

  // Any resize/zoom keeps the focused tile dead-center.
  const applyZoom = (raw) => {
    const newZ = Math.min(2, Math.max(0.05, +(+raw).toFixed(3)));
    if (focusedId && centerOnNode(focusedId, newZ)) return;
    setZoom(newZ);
  };
  // Refs always point at the freshest closures — used by effects/listeners so
  // stale pan/zoom captures can't miscompute the transforms.
  const focusNodeRef = useRef(null);
  focusNodeRef.current = focusNode;
  const centerOnNodeRef = useRef(null);
  centerOnNodeRef.current = centerOnNode;
  const applyZoomRef = useRef(null);
  applyZoomRef.current = applyZoom;
  const zoomStateRef = useRef(1);
  zoomStateRef.current = zoom;
  const focusedIdRef = useRef(null);
  focusedIdRef.current = focusedId;
  const fitRef = useRef(null);
  fitRef.current = fit;

  // Auto-focus the first search match once the expanded tiles have rendered.
  useEffect(() => {
    const q = search.trim();
    if (!q) return;
    const t = setTimeout(() => {
      const first = searchInfoRef.current?.matches ? [...searchInfoRef.current.matches][0] : null;
      if (first) focusNodeRef.current?.(first);
    }, 450);
    return () => clearTimeout(t);
  }, [search]);

  const onTileClick = (e, nodeId) => {
    // Let buttons / links / inputs handle their own click.
    if (e.target.closest("button, a, input, select, textarea, label")) return;
    // If the user just dragged the canvas, swallow the click instead of focusing.
    if (draggedRef.current) { draggedRef.current = false; return; }
    if (focusedId === nodeId) {
      // Second click on the same tile → return to fit-to-screen.
      setFocusedId(null);
      fit();
    } else {
      focusNode(nodeId);
    }
  };

  // ----- branch collapse / expand -----
  const countDescendants = (id) => {
    let c = 0;
    const stack = [...childrenOf(id)];
    while (stack.length) { const n = stack.pop(); c++; stack.push(...childrenOf(n.id)); }
    return c;
  };
  const toggleCollapse = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const collapseAll = () => setCollapsed(new Set(nodes.filter((n) => childrenOf(n.id).length > 0).map((n) => n.id)));
  const expandAll = () => setCollapsed(new Set());

  // auto-fit when the tree changes, the chart is re-opened, or the view mode flips
  useEffect(() => {
    const id = requestAnimationFrame(() => fit());
    return () => cancelAnimationFrame(id);
  }, [nodes.length, chartOpen, viewMode]);

  // full-screen mode: lock page scroll, allow ESC to exit, re-center the focused
  // tile (or re-fit) after the viewport resizes
  useEffect(() => {
    document.body.style.overflow = fullscreen ? "hidden" : "";
    const t = setTimeout(() => {
      if (focusedIdRef.current) centerOnNodeRef.current?.(focusedIdRef.current, zoomStateRef.current);
      else fitRef.current?.();
    }, 90);
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    if (fullscreen) window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [fullscreen]);

  // Window/container resize → keep the focused tile centered (else re-fit).
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (focusedIdRef.current) centerOnNodeRef.current?.(focusedIdRef.current, zoomStateRef.current);
        else fitRef.current?.();
      }, 150);
    };
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, []);

  // wheel-to-zoom (non-passive so we can prevent page scroll over the canvas)
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const next = Math.max(0.25, zoomStateRef.current - e.deltaY * 0.0015);
      applyZoomRef.current?.(next);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [chartOpen, loading, nodes.length]);

  const onMouseDown = (e) => { dragRef.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y }; draggedRef.current = false; setDragging(true); };
  const onMouseMove = (e) => {
    if (!dragging || !dragRef.current) return;
    const d = dragRef.current;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!draggedRef.current && (dx * dx + dy * dy) > 25) draggedRef.current = true;
    setPan({ x: d.px + dx, y: d.py + dy });
  };
  const stopDrag = () => {
    if (draggedRef.current && focusedId) setFocusedId(null);
    setDragging(false);
  };

  // ----- CRUD -----
  const blocked = (() => {
    if (!editingId) return new Set();
    const kidsMap = {};
    for (const n of nodes) {
      const key = n.parent_id || "root";
      if (!kidsMap[key]) kidsMap[key] = [];
      kidsMap[key].push(n.id);
    }
    const out = new Set([editingId]);
    const stack = [...(kidsMap[editingId] || [])];
    while (stack.length) {
      const c = stack.pop();
      if (out.has(c)) continue;
      out.add(c);
      if (kidsMap[c]) stack.push(...kidsMap[c]);
    }
    return out;
  })();

  const resetGrant = () => { setGrant({ role: "participant", method: "invite", loading: false, result: null, error: "" }); setCopied(false); setLinkEmail(""); setLinkError(""); };
  const openEdit = (n) => {
    setEditingId(n.id);
    setForm({
      name: n.name || "",
      parent_id: n.parent_id || "",
      wallet_name: n.wallet_name || "",
      amount: n.amount != null ? String(n.amount) : "",
      current_value: n.current_value != null ? String(n.current_value) : "",
      rank: n.rank || "",
      invite_link: n.invite_link || "",
      email: n.email || "",
      country: n.country || "",
      state: n.state || "",
      wallet_kind: n.wallet_kind || "",
      forecast_date: n.forecast_date || "",
      status: n.status || "",
      affiliate_name: n.affiliate_name || "",
    });
    setError(""); resetGrant(); setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(EMPTY); setError(""); resetGrant(); };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  const doRevoke = async () => {
    const who = editingNode?.email || "this member";
    if (!window.confirm(`Unlink ${who} from this tile?\n\nThe account stays intact (they can still log in and be linked to a different tile). Only this tile's link is cleared. To archive the account itself, use Members → Archive.`)) return;
    setRevoking(true);
    try {
      await api.post(`/org-nodes/${editingId}/revoke-login`);
      setForm((f) => ({ ...f, email: "" }));
      resetGrant();
      await fetchNodes();
    } catch (err) {
      setGrant((g) => ({ ...g, error: formatApiError(err.response?.data?.detail) }));
    } finally { setRevoking(false); }
  };

  const doLink = async () => {
    if (!linkEmail.trim()) return;
    setLinking(true); setLinkError("");
    try {
      await api.post(`/org-nodes/${editingId}/link-user`, { email: linkEmail.trim() });
      // Refresh nodes AND the revenue rollup so the new chips light up immediately.
      const [n, r] = await Promise.all([
        api.get("/org-nodes"),
        api.get("/admin/org-revenue-rollup").catch(() => ({ data: {} })),
      ]);
      setAllNodes(n.data);
      setRevenueByUser(r.data || {});
      setLinkEmail("");
    } catch (err) {
      setLinkError(formatApiError(err.response?.data?.detail) || "Could not link account.");
    } finally { setLinking(false); }
  };

  const doImpersonate = async (node) => {
    if (!node.user_id) return;
    if (!window.confirm(
      `View the app as ${node.email || node.name}?\n\nYou'll see exactly what they see. The session is read-only — you can't make changes. Every impersonation is logged.`,
    )) return;
    setImperBusy(node.id);
    try {
      await startImpersonation(node.user_id);
      toast.success(`Now viewing as ${node.email || node.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start impersonation.");
    } finally {
      setImperBusy(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault(); setError("");
    const payload = {
      name: form.name.trim(),
      parent_id: form.parent_id || null,
      wallet_name: form.wallet_name.trim() || null,
      amount: form.amount === "" ? null : parseFloat(form.amount),
      current_value: form.current_value === "" ? null : parseFloat(form.current_value),
      rank: form.rank || null,
      invite_link: form.invite_link.trim() || null,
      email: form.email.trim() || null,
      country: form.country.trim(),
      state: form.state.trim(),
      wallet_kind: form.wallet_kind || null,
      forecast_date: form.forecast_date || null,
      status: form.status || null,
      affiliate_name: form.affiliate_name.trim() || null,
    };
    try {
      const res = editingId
        ? await api.put(`/org-nodes/${editingId}`, payload)
        : await api.post("/org-nodes", payload);
      const data = res.data;
      // If status=Lead triggered the auto-CRM-+-7-day-invite flow, surface the
      // temp password to the admin so they can share it before the modal closes.
      if (data?.lead_temp_password) {
        toast.success(`Lead invite ready — temp password: ${data.lead_temp_password}`, { duration: 12000 });
      } else if (data?.lead_auto_deactivate_at) {
        toast.success("Added to CRM · 7-day login extended.", { duration: 6000 });
      }
      closeModal();
      fetchNodes();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this person? Anyone under them moves up to their manager.")) return;
    try {
      await api.delete(`/org-nodes/${id}`);
      if (editingId === id) closeModal();
      fetchNodes();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  // ----- downloads -----
  const stamp = () => todayIn();

  const downloadExcel = () => {
    // Excel and CSV must share the SAME 13 upload columns so admins aren't
    // confused when they switch formats. Excel is a superset — it *also*
    // appends 3 read-only rollup columns for at-a-glance analysis. Extra
    // columns are safely ignored by the upload parser, so the workbook can
    // still be re-uploaded via `mode=update` for a lossless round-trip.
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const parentRef = (n) => {
      const p = byId.get(n.parent_id);
      return p ? (p.wallet_name || p.name || "") : "";
    };
    const rows = flatten().map(({ node, level }) => ({
      // ---- 13 CSV round-trip columns (SAME KEYS + ORDER as the CSV export) ----
      label: node.name || "",
      wallet: node.wallet_name || "",
      account_email: node.email || "",
      parent_wallet: parentRef(node),
      country: node.country || "",
      state: node.state || "",
      wallet_type: node.wallet_kind || "",
      forecast_date: node.forecast_date || "",
      status: node.status || "",
      rank: node.rank || "",
      principal: node.amount != null ? node.amount : "",
      current_value: node.current_value != null ? node.current_value : "",
      carry_pact_link: node.invite_link || "",
      // ---- Read-only rollup columns (ignored on re-upload) ----
      "· Level": level,
      "· Direct Reports": childrenOf(node.id).length,
      "· Total Downline": countDescendants(node.id),
      "· Downline $": Math.round(effPerf(node)),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    // Header widths match column importance (editable columns are wider, rollups tighter).
    ws["!cols"] = [
      { wch: 26 }, { wch: 16 }, { wch: 28 }, { wch: 20 },     // label, wallet, email, parent
      { wch: 16 }, { wch: 16 }, { wch: 12 }, { wch: 14 },     // country, state, wallet_type, forecast_date
      { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 14 },      // status, rank, principal, current_value
      { wch: 44 },                                             // carry_pact_link
      { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 14 },      // rollup cols
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Org Chart");
    XLSX.writeFile(wb, `BotChainCP-Org-Chart-${stamp()}.xlsx`);
  };

  // Downline $ = the wallet's own CaryPact "team effective performance"
  // (teamLockUsdtAmount = deposits + staking value). Falls back to the computed
  // subtree sum for tiles that haven't synced yet.
  const effPerf = (node) => {
    const v = Number(node?.cp_data?.teamLockUsdtAmount);
    return Number.isFinite(v) && v > 0 ? v : (downline.totals[node.id] || node.amount || 0);
  };

  // Biggest leg per sibling group: among 2+ direct children of the same upline,
  // the child whose team performance $ is largest gets highlighted on the chart.
  const bigLegIds = useMemo(() => {
    const out = new Set();
    const doneParents = new Set();
    nodes.forEach((n) => {
      const pid = n.parent_id;
      if (!pid || doneParents.has(pid)) return;
      doneParents.add(pid);
      const sibs = childrenOf(pid);
      if (sibs.length < 2) return;
      let best = null, bestV = 0;
      sibs.forEach((s) => { const v = effPerf(s); if (v > bestV) { bestV = v; best = s; } });
      if (best && bestV > 0) out.add(best.id);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, downline]);

  const downloadExcelStructured = () => {
    // Presentation-style report matching the classic org sheet: indented member
    // names + downline rollups. Read-only — not meant for re-upload.
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const rows = flatten().map(({ node, level }) => ({
      "Level": level,
      "Member": `${"    ".repeat(level)}${node.name || ""}`,
      "Reports To": node.parent_id ? ((byId.get(node.parent_id) || {}).name || "—") : "—",
      "Wallet": node.wallet_name || "",
      "Amount (USDT)": node.amount != null ? node.amount : "",
      "Direct Reports": childrenOf(node.id).length,
      "Total Downline": countDescendants(node.id),
      "Downline $": Math.round(effPerf(node)),
      "Invite Link": node.invite_link || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 6 }, { wch: 34 }, { wch: 20 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 44 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Org Chart");
    XLSX.writeFile(wb, `BotChainCP-Org-Chart-Structured-${stamp()}.xlsx`);
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    doc.setFontSize(18); doc.setTextColor(15, 18, 38);
    doc.text("BotChainCP — Team Hierarchy", 40, 48);
    doc.setFontSize(10); doc.setTextColor(100, 116, 139);
    doc.text(`Generated ${today}`, 40, 66);
    doc.text(`People: ${nodes.length}   ·   Top-level: ${roots.length}   ·   Depth: ${maxDepth + 1}   ·   Total amount: $${totalAmount.toLocaleString()}`, 40, 82);

    const body = flatten().map(({ node, level }) => {
      const prefix = level === 0 ? "" : "  ".repeat(level - 1) + "└ ";
      return [
        prefix + node.name,
        `L${level}`,
        node.wallet_name || "—",
        node.amount != null ? `$${Number(node.amount).toLocaleString()}` : "—",
        String(childrenOf(node.id).length),
      ];
    });

    autoTable(doc, {
      startY: 100,
      head: [["Member", "Level", "Wallet", "Amount", "Direct"]],
      body,
      styles: { fontSize: 9, cellPadding: 5, textColor: [30, 41, 59] },
      headStyles: { fillColor: [124, 58, 237], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      columnStyles: {
        0: { font: "courier", cellWidth: 230 },
        1: { cellWidth: 45, halign: "center" },
        2: { cellWidth: 90 },
        3: { cellWidth: 80, halign: "right" },
        4: { cellWidth: 50, halign: "center" },
      },
      margin: { left: 40, right: 40 },
    });

    doc.save(`BotChainCP-Org-Chart-${stamp()}.pdf`);
  };

  // ----- High-resolution VECTOR print poster · FULL-SIZE readable tiles -----
  // Tiles are drawn at a fixed readable size; if the tree is bigger than one
  // 36×48 sheet it's split across multiple sheets (with overlap + labels) to
  // tape together. Everything is vector, so it's crisp at any zoom / print size.
  const downloadPoster = async () => {
    if (posterBusy) return;
    setPosterBusy(true);
    const prevCollapsed = collapsed, prevZoom = zoom, prevPan = pan;
    const tid = toast.loading("Building your full-size vector poster…");
    try {
      setCollapsed(new Set());
      setZoom(1);
      setPan({ x: 0, y: 0 });
      await new Promise((r) => setTimeout(r, 1300));

      const treeEl = treeRef.current?.querySelector(".org-tree");
      if (!treeEl) throw new Error("Chart not ready");
      const treeRect = treeEl.getBoundingClientRect();
      const nodeById = new Map(nodes.map((n) => [n.id, n]));

      const tiles = [];
      let natW = 0, natH = 0, wSum = 0;
      treeEl.querySelectorAll("[data-node-id]").forEach((el) => {
        const id = el.getAttribute("data-node-id");
        const n = nodeById.get(id);
        if (!n) return;
        const r = el.getBoundingClientRect();
        const x = r.left - treeRect.left, y = r.top - treeRect.top;
        tiles.push({ id, n, x, y, w: r.width, h: r.height });
        natW = Math.max(natW, x + r.width);
        natH = Math.max(natH, y + r.height);
        wSum += r.width;
      });
      if (!tiles.length || !natW || !natH) throw new Error("No tiles measured");
      const posById = new Map(tiles.map((t) => [t.id, t]));

      // Fixed readable scale: make the average tile ~2.4in wide.
      const avgWpx = wSum / tiles.length;
      const s = 2.4 / avgWpx;                 // inches per layout-px
      const totalW = natW * s, totalH = natH * s;

      // 36×48 sheets, oriented to need the fewest pages.
      const margin = 0.5, overlap = 0.5, titleH = 1.6;
      const plan = (pw, ph) => {
        const stepX = pw - margin * 2 - overlap;
        const firstStepY = ph - margin - titleH - overlap;   // page 0,0 loses title height
        const stepY = ph - margin * 2 - overlap;
        let rows = 1, consumed = firstStepY;
        while (consumed < totalH) { consumed += stepY; rows++; }
        const cols = Math.max(1, Math.ceil((totalW - overlap) / stepX));
        return { pw, ph, stepX, stepY, firstStepY, cols, rows, pages: cols * rows };
      };
      const land = plan(48, 36), port = plan(36, 48);
      const P = land.pages <= port.pages ? land : port;
      const landscape = P.pw === 48;
      if (P.pages > 60) throw new Error("Too many pages");

      const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "in", format: [36, 48], compress: true });
      const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
      const chartLabel = chart === "carypact_live" ? "CaryPact Live" : "My Org";

      const STATUS = {
        lead: [234, 88, 12], onboarded: [37, 99, 235], active: [0, 134, 58],
        paused: [217, 119, 6], inactive: [220, 38, 38],
      };
      const clampText = (str, maxIn, fontPt) => {
        doc.setFontSize(fontPt);
        let out = String(str ?? "");
        if (doc.getTextWidth(out) <= maxIn) return out;
        while (out.length > 1 && doc.getTextWidth(out + "…") > maxIn) out = out.slice(0, -1);
        return out + "…";
      };

      let pageIdx = 0;
      for (let row = 0; row < P.rows; row++) {
        for (let col = 0; col < P.cols; col++) {
          if (pageIdx > 0) doc.addPage([36, 48], landscape ? "landscape" : "portrait");
          pageIdx++;
          const topPad = row === 0 ? titleH : 0;
          // Global-inch region shown on this sheet:
          const gx0 = col * P.stepX;
          const gy0 = row === 0 ? 0 : (P.firstStepY + (row - 1) * P.stepY);
          // Map a global-inch coord to this page's coords.
          const PX = (gin) => margin + (gin - gx0);
          const PY = (gin) => margin + topPad + (gin - gy0);
          const X = (px) => PX(px * s);
          const Y = (py) => PY(py * s);

          // Title on the very first sheet.
          if (row === 0 && col === 0) {
            doc.setFont("helvetica", "bold"); doc.setFontSize(52); doc.setTextColor(15, 18, 38);
            doc.text("BotChainCP — Team Hierarchy", margin, margin + 0.62);
            doc.setFont("helvetica", "normal"); doc.setFontSize(22); doc.setTextColor(100, 116, 139);
            doc.text(`${chartLabel}  ·  ${nodes.length} wallets  ·  ${maxDepth + 1} levels  ·  Generated ${today}`, margin, margin + 1.06);
            doc.setDrawColor(124, 58, 237); doc.setLineWidth(0.035);
            doc.line(margin, margin + titleH - 0.2, P.pw - margin, margin + titleH - 0.2);
          }
          // Sheet label (for multi-page assembly).
          if (P.pages > 1) {
            doc.setFont("helvetica", "normal"); doc.setFontSize(13); doc.setTextColor(150, 158, 180);
            doc.text(`Sheet R${row + 1}·C${col + 1}  (of ${P.rows}×${P.cols})`, P.pw - margin, P.ph - margin + 0.1, { align: "right" });
          }

          // Connectors behind tiles.
          doc.setDrawColor(150, 158, 180); doc.setLineWidth(0.012);
          tiles.forEach((t) => {
            const parent = t.n.parent_id ? posById.get(t.n.parent_id) : null;
            if (!parent) return;
            const pbx = X(parent.x + parent.w / 2), pby = Y(parent.y + parent.h);
            const ctx = X(t.x + t.w / 2), cty = Y(t.y);
            const midY = (pby + cty) / 2;
            doc.line(pbx, pby, pbx, midY);
            doc.line(pbx, midY, ctx, midY);
            doc.line(ctx, midY, ctx, cty);
          });

          // Tiles.
          tiles.forEach((t) => {
            const x = X(t.x), y = Y(t.y), w = t.w * s, h = t.h * s;
            const sk = (t.n.status || "").toLowerCase();
            const accent = STATUS[sk] || [124, 58, 237];
            const rad = Math.min(0.1, h * 0.12);
            doc.setDrawColor(214, 218, 230); doc.setLineWidth(0.01);
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(x, y, w, h, rad, rad, "FD");
            doc.setFillColor(accent[0], accent[1], accent[2]);
            doc.roundedRect(x, y, Math.max(0.05, w * 0.03), h, rad, rad, "F");

            const padX = w * 0.06, innerW = w - padX - w * 0.05;
            let cy = y + 0.05;
            const namehPt = Math.max(9, Math.min(h * 72 * 0.16, 15));
            doc.setFont("helvetica", "bold"); doc.setTextColor(15, 18, 38); doc.setFontSize(namehPt);
            cy += namehPt / 72;
            doc.text(clampText(t.n.name, innerW, namehPt), x + padX, cy);

            const subPt = Math.max(7, Math.min(h * 72 * 0.11, 11));
            const gap = subPt / 72 * 1.5;
            doc.setFont("helvetica", "normal");
            const wallet = t.n.wallet_name
              ? (t.n.wallet_name.startsWith("0x") && t.n.wallet_name.length > 14
                  ? `${t.n.wallet_name.slice(0, 6)}…${t.n.wallet_name.slice(-4)}` : t.n.wallet_name)
              : "No wallet";
            cy += gap;
            doc.setTextColor(120, 128, 150); doc.setFontSize(subPt);
            doc.text(clampText(wallet, innerW, subPt), x + padX, cy);

            const principal = t.n.amount != null ? `$${Number(t.n.amount).toLocaleString()}` : null;
            const bits = [t.n.rank || null, principal].filter(Boolean).join("   ·   ");
            if (bits) {
              cy += gap;
              doc.setTextColor(0, 120, 55); doc.setFont("helvetica", "bold"); doc.setFontSize(subPt);
              doc.text(clampText(bits, innerW, subPt), x + padX, cy);
            }
          });
        }
      }

      doc.save(`BotChainCP-Org-Poster-${landscape ? "48x36" : "36x48"}-${P.pages > 1 ? `${P.pages}sheets-` : ""}${stamp()}.pdf`);
      toast.success(
        P.pages > 1
          ? `Poster ready — ${P.pages} sheets (${P.rows}×${P.cols}) at full-size tiles. Print all pages and tape them together (0.5″ overlap).`
          : `Poster ready — full-size vector tiles, crisp at any zoom. Send straight to your print shop.`,
        { id: tid, duration: 10000 });
    } catch (err) {
      toast.error("Poster export failed — wait for the tree to finish loading, then try again.", { id: tid });
    } finally {
      setCollapsed(prevCollapsed);
      setZoom(prevZoom);
      setPan(prevPan);
      setPosterBusy(false);
    }
  };

  // ----- CSV bulk upload (admin-only) -----
  const openUpload = () => {
    setUploadFile(null); setUploadResult(null); setUploadError("");
    setUploadMode("skip"); setUploadOpen(true);
  };
  const closeUpload = () => { setUploadOpen(false); };

  const downloadCsvTemplate = async () => {
    try {
      const res = await api.get("/org-nodes/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "BotChainCP-org-chart-template.csv";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not download template.");
    }
  };

  // Round-trip: export the CURRENT chart in the exact same CSV schema the
  // upload endpoint accepts — admin edits it in Excel/Sheets and re-uploads.
  const downloadCsvRoundTrip = async () => {
    try {
      const res = await api.get("/org-nodes/csv-export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `BotChainCP-org-chart-${stamp()}.csv`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Downloaded — edit the CSV and re-upload with 'Update duplicates' to apply changes.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not export CSV.");
    }
  };

  const submitUpload = async () => {
    if (!uploadFile) { setUploadError("Pick a CSV file first."); return; }
    setUploading(true); setUploadError(""); setUploadResult(null);
    try {
      const fd = new FormData(); fd.append("file", uploadFile);
      const { data } = await api.post(`/org-nodes/upload-csv?mode=${uploadMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
      const parts = [];
      if (data.created > 0) parts.push(`${data.created} created`);
      if (data.updated > 0) parts.push(`${data.updated} updated`);
      if (data.skipped_duplicates > 0) parts.push(`${data.skipped_duplicates} skipped`);
      if (parts.length > 0) {
        toast.success(`Import done · ${parts.join(" · ")}`);
        await fetchNodes();
      } else {
        toast.info("No rows applied.");
      }
    } catch (err) {
      setUploadError(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // ----- Snapshots (rolling last 5 backup + restore) -----
  const fetchSnapshots = async () => {
    try {
      const { data } = await api.get("/org-snapshots");
      setSnapshots(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load snapshots.");
    }
  };
  const openSnapshots = async () => { await fetchSnapshots(); setSnapshotsOpen(true); };
  const closeSnapshots = () => setSnapshotsOpen(false);

  const doRestore = async (snap) => {
    const stamp = new Date(snap.created_at).toLocaleString();
    if (!window.confirm(
      `Restore org chart to the snapshot from ${stamp}?\n\n` +
      `This will REPLACE every current tile with the ${snap.count} tiles frozen at that time. ` +
      `A fresh snapshot of the current state is taken first, so this restore is itself undoable.`,
    )) return;
    setRestoringId(snap.id);
    try {
      const { data } = await api.post(`/org-snapshots/${snap.id}/restore`);
      toast.success(`Restored ${data.restored_count} tiles from ${stamp}`);
      closeSnapshots();
      await fetchNodes();
      await fetchSnapshots();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Restore failed.");
    } finally {
      setRestoringId(null);
    }
  };

  // stop a control click from being captured by the canvas pan-drag handler
  const noDrag = (e) => e.stopPropagation();

  // NOTE: recursive render *function* (not a self-referencing component) on purpose —
  // a recursive component crashes the @emergentbase/visual-edits babel plugin.
  const renderNode = (node, level = 0) => {
    const kids = childrenOf(node.id);
    const hasKids = kids.length > 0;
    const isCollapsed = effectiveCollapsed(node.id);
    const hidden = isCollapsed ? countDescendants(node.id) : 0;
    const isSearchHit = searchInfo.matches?.has(node.id);
    const isSearchDim = searchInfo.matches != null && !isSearchHit;
    const statusKey = (node.status || "").toLowerCase();
    const statusClass = ["lead", "onboarded", "active", "paused", "inactive"].includes(statusKey)
      ? ` is-status-${statusKey}` : "";

    // Simple view — just the name + tree lines (status colour kept as a left accent).
    if (viewMode === "simple") {
      return (
        <li key={node.id}>
          <div
            className={`org-node org-node-simple${focusedId === node.id ? " is-focused" : ""}${isSearchHit ? " is-search-hit" : ""}${isSearchDim ? " is-search-dim" : ""}${bigLegIds.has(node.id) ? " is-big-leg" : ""}${statusClass}`}
            data-testid={`org-node-${node.id}`}
            data-node-id={node.id}
            onClick={(e) => onTileClick(e, node.id)}
            title={node.name}
          >
            <span className="org-node-simple-name" data-testid={`org-node-name-${node.id}`}>{node.name}</span>
            {hasKids ? (
              <button
                onClick={() => toggleCollapse(node.id)}
                onMouseDown={noDrag}
                className="org-node-chevron"
                title={isCollapsed ? `Expand branch (${hidden} hidden)` : "Collapse branch"}
                data-testid={`org-collapse-${node.id}`}
              >
                <ChevronDown size={13} style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform .15s ease" }} />
              </button>
            ) : null}
          </div>
          {hasKids && !isCollapsed ? <ul>{kids.map((k) => renderNode(k, level + 1))}</ul> : null}
        </li>
      );
    }

    return (
      <li key={node.id}>
        <div
          className={`org-node${focusedId === node.id ? " is-focused" : ""}${isSearchHit ? " is-search-hit" : ""}${isSearchDim ? " is-search-dim" : ""}${bigLegIds.has(node.id) ? " is-big-leg" : ""}${statusClass}`}
          data-testid={`org-node-${node.id}`}
          data-node-id={node.id}
          onClick={(e) => onTileClick(e, node.id)}
          title={focusedId === node.id ? "Click again to fit-to-screen" : "Click to focus on this tile"}
        >
          {/* header: name + add-downline + chevron toggle */}
          <div className="org-node-head">
            <div className="org-node-name" data-testid={`org-node-name-${node.id}`} title={node.name}>{node.name}</div>
            <div className="org-node-head-actions">
              {hasKids ? (
                <button
                  onClick={() => toggleCollapse(node.id)}
                  onMouseDown={noDrag}
                  className="org-node-chevron"
                  title={isCollapsed ? `Expand branch (${hidden} hidden)` : "Collapse branch"}
                  data-testid={`org-collapse-${node.id}`}
                >
                  <ChevronDown size={15} style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform .15s ease" }} />
                </button>
              ) : null}
            </div>
          </div>

          {/* body */}
          <div className="org-node-body">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="org-level-badge" data-testid={`org-level-${node.id}`}>Level {node.true_level ?? level}</span>
              {bigLegIds.has(node.id) && (
                <span className="chip text-[11px]" style={{ background: "rgba(217,119,6,0.16)", color: "#B45309", fontWeight: 800 }}
                  title="Biggest leg under this upline (largest team performance $ among siblings)"
                  data-testid={`org-bigleg-${node.id}`}>
                  <Crown size={10} className="inline -mt-0.5 mr-0.5" /> Big leg
                </span>
              )}
              {node.status && (
                <span
                  className="chip text-[11px]"
                  style={{
                    background:
                      statusKey === "lead" ? "rgba(245,158,11,0.14)" :
                      statusKey === "onboarded" ? "rgba(64,96,255,0.12)" :
                      statusKey === "active" ? "rgba(16,185,129,0.12)" :
                      statusKey === "paused" ? "rgba(180,83,9,0.14)" :
                      statusKey === "inactive" ? "rgba(220,38,38,0.14)" :
                      "rgba(15,18,38,0.08)",
                    color:
                      statusKey === "lead" ? "#B45309" :
                      statusKey === "onboarded" ? "#3B4EDB" :
                      statusKey === "active" ? "#047857" :
                      statusKey === "paused" ? "#92400E" :
                      statusKey === "inactive" ? "#B91C1C" :
                      "#3F3F46",
                    fontWeight: 700,
                  }}
                  data-testid={`org-status-${node.id}`}
                  title={`Lifecycle status: ${node.status}`}
                >
                  {node.status}
                </span>
              )}
              {node.account_status === "active" ? (
                <span className="org-acct-badge org-acct-active" title={node.email || ""} data-testid={`org-acct-${node.id}`}>{node.account_role === "affiliate_manager" ? "Leader" : "Member"}</span>
              ) : node.account_status === "invited" ? (
                <span className="org-acct-badge org-acct-invited" title={node.email || ""} data-testid={`org-acct-${node.id}`}>Invited</span>
              ) : null}
              {node.reset_requested ? (
                <span className="org-acct-badge org-acct-invited" title="This member asked to reset their password — regenerate access for them" data-testid={`org-reset-${node.id}`}>Reset requested</span>
              ) : null}
            </div>
            <div className="org-node-meta"><Wallet size={12} /> {node.wallet_name || "No wallet"}</div>
            {(() => {
              const cpLink = node.invite_link ||
                (node.cp_data?.promotionCode ? `https://app.carypact.com/?inviteCode=${node.cp_data.promotionCode}` : null);
              if (!cpLink) return null;
              const code = (cpLink.match(/inviteCode=([^&]+)/) || [])[1] || cpLink.replace(/^https?:\/\//, "").slice(0, 24);
              return (
                <div className="org-node-meta" data-testid={`org-cplink-${node.id}`}>
                  <Link2 size={12} />
                  <a
                    href={cpLink} target="_blank" rel="noopener noreferrer" onMouseDown={noDrag}
                    className="hover:underline truncate" style={{ color: "#4060FF", fontWeight: 600 }}
                    title={`Open CaryPact invite link\n${cpLink}`}
                    data-testid={`org-cplink-open-${node.id}`}
                  >
                    CaryPact · {code}
                  </a>
                  <button
                    onClick={() => { navigator.clipboard.writeText(cpLink); toast.success("CaryPact link copied"); }}
                    onMouseDown={noDrag} title="Copy CaryPact link" className="text-ink-3 hover:text-[#4060FF] transition-colors"
                    data-testid={`org-cplink-copy-${node.id}`}
                  >
                    <Copy size={11} />
                  </button>
                </div>
              );
            })()}
            {(node.country || node.state) && (
              <div className="org-node-meta" data-testid={`org-locale-${node.id}`} title="Country · State / Province">
                <MapPin size={12} /> {[node.state, node.country].filter(Boolean).join(", ") || "—"}
              </div>
            )}
            {(node.wallet_kind || node.forecast_date || node.rank) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid={`org-kind-${node.id}`}>
                {node.rank && (
                  <span
                    className="chip text-[11px]"
                    style={{ background: "rgba(217,119,6,0.10)", color: "#B45309", fontWeight: 700 }}
                    title="CaryPact V-tier rank"
                    data-testid={`org-rank-${node.id}`}
                  >
                    {node.rank}
                  </span>
                )}
                {node.wallet_kind && (
                  <span
                    className="chip text-[11px]"
                    style={{
                      background: node.wallet_kind === "Mining" ? "rgba(64,96,255,0.10)" :
                                  node.wallet_kind === "Staking" ? "rgba(194,24,91,0.10)" :
                                  "rgba(124,58,237,0.10)",
                      color: node.wallet_kind === "Mining" ? "#4060FF" :
                             node.wallet_kind === "Staking" ? "#C2185B" :
                             "#7C3AED",
                    }}
                  >
                    {node.wallet_kind}
                  </span>
                )}
                {node.forecast_date && (
                  <span className="chip text-[11px] chip-blue" title="Projected break-even / recovery date">
                    Target {node.forecast_date}
                  </span>
                )}
              </div>
            )}
            {node.cp_data && Object.keys(node.cp_data).length > 0 && (() => {
              const all = Object.entries(node.cp_data);
              const entries = all
                .map(([k, v]) => [k, fmtCpVal(v)])
                .filter(([, v]) => v !== null);
              if (entries.length === 0) return null;
              const blanks = all.length - entries.length;
              const open = cpOpen.has(node.id);
              return (
                <div className="mt-1.5" data-testid={`org-cp-data-${node.id}`}>
                  <button
                    onClick={() => toggleCpOpen(node.id)}
                    onMouseDown={noDrag}
                    className="chip text-[10px] inline-flex items-center gap-1 hover:opacity-75 transition-opacity"
                    style={{ background: "rgba(124,58,237,0.10)", color: "#7C3AED", fontWeight: 700 }}
                    title={`${entries.length} of ${all.length} CaryPact fields have a value${node.cp_synced_at ? ` · synced ${node.cp_synced_at.slice(0, 16).replace("T", " ")}` : ""}`}
                    data-testid={`org-cp-toggle-${node.id}`}
                  >
                    <Database size={9} /> CP data ({entries.length}<span style={{ opacity: 0.55 }}>/{all.length}</span>)
                    <ChevronDown size={9} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s ease" }} />
                  </button>
                  {open && (
                    <div
                      className="mt-1.5 rounded-lg px-2.5 py-2 space-y-0.5"
                      style={{ background: "rgba(124,58,237,0.05)", border: "1px solid rgba(124,58,237,0.15)", maxHeight: 180, overflowY: "auto" }}
                      onMouseDown={noDrag}
                      data-testid={`org-cp-panel-${node.id}`}
                    >
                      {entries.map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-3 text-[10.5px] leading-snug">
                          <span className="font-mono text-ink-3 truncate" title={k}>{k}</span>
                          <span className="font-mono font-semibold text-ink text-right" title={String(node.cp_data[k])}>{v}</span>
                        </div>
                      ))}
                      <div className="pt-1.5 mt-1 text-[10px] leading-snug" style={{ borderTop: "1px solid rgba(124,58,237,0.15)", color: "#5A607F" }}
                        data-testid={`org-cp-footer-${node.id}`}>
                        {blanks > 0 && <div>{blanks} more field{blanks === 1 ? "" : "s"} came back empty or zero — hidden rather than shown as a fake 0.</div>}
                        {!node.cp_own_token && (
                          <div className="mt-1">
                            <b>Fewer fields than the top wallet?</b> CaryPact only reveals a wallet&apos;s profile, BOT mining,
                            hashrate and node rewards to that wallet&apos;s <b>own</b> live session — the tree walk can&apos;t fetch them.
                            Reconnect this wallet once and they fill in.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {downline.counts[node.id] > 0 && (
              <div className="org-node-downline" data-testid={`org-downline-${node.id}`} title={`Community effective performance: $${effPerf(node).toLocaleString(undefined, { maximumFractionDigits: 0 })} (deposits + staking value, straight from CaryPact) · ${downline.counts[node.id]} people underneath`}>
                <span className="org-downline-leg" /> Downline ·{" "}
                <b className="num">{fmtMoney(effPerf(node))}</b>
                <span className="org-downline-dot" />
                {downline.counts[node.id]} people
              </div>
            )}

            {node.cp_data && ("totalLockUsdtAmount" in node.cp_data || "totalLockCaAmount" in node.cp_data) ? (() => {
              const vip = vipInfo(node.cp_data);
              return (
              <>
              <div className="org-stream-row" data-testid={`org-cp-stats-${node.id}`}>
                <span className="org-stream-chip org-stream-mining" title={`Mining — USDT locked: $${Number(node.cp_data.totalLockUsdtAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} data-testid={`org-cp-mining-${node.id}`}>
                  Mining {fmtMoney(Number(node.cp_data.totalLockUsdtAmount || 0))}
                </span>
                <span className="org-stream-chip org-stream-staking" title={`Staking — CA locked: ${Number(node.cp_data.totalLockCaAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })} CA ≈ $${(Number(node.cp_data.totalLockCaAmount || 0) * (caPriceLive || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} data-testid={`org-cp-staking-${node.id}`}>
                  Staked {caPriceLive ? `$${(Number(node.cp_data.totalLockCaAmount || 0) * caPriceLive).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `${Number(node.cp_data.totalLockCaAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} CA`}
                </span>
                {Number(node.cp_data.totalPowerAmount || 0) > 0 && (
                  <span className="org-stream-chip org-stream-community" title={`Computing power (hashrate): ${Number(node.cp_data.totalPowerAmount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} data-testid={`org-cp-power-${node.id}`}>
                    Power {Number(node.cp_data.totalPowerAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
              {vip.next && (
                <div
                  className="text-[10px] font-mono mt-1"
                  style={{ color: "#8A8FA8" }}
                  title={`To reach V${vip.next.level}: personal performance ≥ $${vip.next.reqPersonal.toLocaleString()} AND leg effective performance ≥ $${vip.next.reqLeg.toLocaleString()}\nCurrent: $${vip.personal.toLocaleString()} personal · $${vip.leg.toLocaleString()} leg`}
                  data-testid={`org-vip-next-${node.id}`}
                >
                  → V{vip.next.level}: {vip.next.needPersonal > 0 ? `+${fmtUsdShort(vip.next.needPersonal)} personal` : "personal ✓"} · {vip.next.needLeg > 0 ? `+${fmtUsdShort(vip.next.needLeg)} leg` : "leg ✓"}
                </div>
              )}
              </>
              );
            })() : (
            <div className="org-stream-row" data-testid={`org-revenue-${node.id}`}>
              <span className="org-stream-chip org-stream-mining" title={`Mining (this tile + downline): $${(downline.mining[node.id] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}${!node.user_id ? " · tile not linked to an account" : ""}`} data-testid={`org-mining-${node.id}`}>
                M {fmtMoney(downline.mining[node.id] || 0)}
              </span>
              <span className="org-stream-chip org-stream-staking" title={`Staking (this tile + downline): $${(downline.staking[node.id] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}${!node.user_id ? " · tile not linked to an account" : ""}`} data-testid={`org-staking-${node.id}`}>
                S {fmtMoney(downline.staking[node.id] || 0)}
              </span>
              <span className="org-stream-chip org-stream-community" title={`Community (this tile + downline): $${(downline.community[node.id] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}${!node.user_id ? " · tile not linked to an account" : ""}`} data-testid={`org-community-${node.id}`}>
                C {fmtMoney(downline.community[node.id] || 0)}
              </span>
            </div>
            )}

            <div className="org-node-foot">
              <div className="org-node-direct" data-testid={`org-direct-${node.id}`}>
                <Users size={12} /> {kids.length} direct{isCollapsed && hidden ? ` · ${hidden} hidden` : ""}
              </div>
              <div className="org-node-actions">
                {node.user_id && user?.role === "admin" && node.user_id !== user?.id && (
                  <button
                    onClick={() => doImpersonate(node)}
                    onMouseDown={noDrag}
                    disabled={imperBusy === node.id}
                    title={`View as ${node.email || node.name} (read-only)`}
                    data-testid={`org-impersonate-${node.id}`}
                    className="org-icon-btn org-icon-impersonate"
                  >
                    <Eye size={13} />
                  </button>
                )}
                {node.invite_link ? <a href={node.invite_link} onMouseDown={noDrag} target="_blank" rel="noopener noreferrer" title="Invitation link" data-testid={`org-link-${node.id}`} className="org-icon-btn"><Link2 size={13} /></a> : null}
                {node.invite_link ? (
                  <button onClick={() => { navigator.clipboard.writeText(node.invite_link).then(() => toast.success("Invite link copied")).catch(() => {}); }} onMouseDown={noDrag} title="Copy invitation link" data-testid={`org-copy-link-${node.id}`} className="org-icon-btn"><Copy size={13} /></button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {hasKids && !isCollapsed ? <ul>{kids.map((k) => renderNode(k, level + 1))}</ul> : null}
      </li>
    );
  };

  const zoomBtn = "w-8 h-8 rounded-lg flex items-center justify-center text-ink-2 hover:bg-white/70 transition-colors";
  const parentName = nodes.find((n) => n.id === form.parent_id)?.name;
  const editingNode = editingId ? nodes.find((n) => n.id === editingId) : null;
  const acctStatus = editingNode?.account_status || "none";

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><span className="dot" />CaryPact Live · auto-synced mirror</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">My community</h1>
          <p className="text-ink-2 mt-2 text-sm">
            The <span className="font-medium text-ink-1">live mirror</span> of your CaryPact downline — every tile is created by sync, nothing is added by hand. Manage everything about a wallet in <span className="font-medium text-ink-1">Finances → Wallets</span>.
          </p>
        </div>

        {user?.role === "affiliate_manager" && !leaderNoteHidden && (
          <div className="mb-6 rounded-xl p-3.5 flex items-start gap-2.5 fade-up" style={{ background: "rgba(124,58,237,0.07)", border: "1px solid rgba(124,58,237,0.22)" }} data-testid="leader-scope-banner">
            <Eye size={15} className="shrink-0 mt-0.5" style={{ color: "#7C3AED" }} />
            <div className="text-[13px] text-ink-2 leading-relaxed flex-1">
              <b className="text-ink">You're viewing your own team.</b> This chart — and every number on it — covers just your wallet and everyone below you, not the whole community.
            </div>
            <button onClick={() => { localStorage.setItem("leader_scope_note_hidden", "1"); setLeaderNoteHidden(true); }} className="text-ink-3 hover:text-ink transition-colors shrink-0" title="Got it — hide this" data-testid="leader-scope-dismiss">
              <X size={14} />
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <div className="kpi-tile" data-testid="org-kpi-amount">
            <div className="kpi-label">Total Community Effective Performance</div>
            <div className="kpi-num" data-testid="org-kpi-current">${Number(rootCp.teamLockUsdtAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-[11px] text-ink-3 mt-1">from sync · teamLockUsdtAmount</div>
          </div>
          <div className="kpi-tile" data-testid="org-kpi-hashrate-perf">
            <div className="kpi-label">Community Hashrate Deposit Performance</div>
            <div className="kpi-num">${Number(rootCp.teamTotalLockUsdtAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-[11px] text-ink-3 mt-1">from sync · teamTotalLockUsdtAmount</div>
          </div>
          <div className="kpi-tile" data-testid="org-kpi-miner-perf">
            <div className="kpi-label">Community Miner Performance</div>
            <div className="kpi-num">${Number(rootCp.teamMinerPerformanceAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-[11px] text-ink-3 mt-1">from sync · teamMinerPerformanceAmount</div>
          </div>
          <div className="kpi-tile" data-testid="org-kpi-staking-perf">
            <div className="kpi-label">Total Community Staking Performance</div>
            <div className="kpi-num">${Math.max(0, Number(rootCp.teamLockUsdtAmount || 0) - (Number(rootCp.teamTotalLockUsdtAmount || 0) + Number(rootCp.teamMinerPerformanceAmount || 0))).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-[11px] text-ink-3 mt-1">{Number(rootCp.teamLockCaAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} CA · effective − (hashrate + miner)</div>
          </div>
          <div className="kpi-tile" data-testid="org-kpi-directs">
            <div className="kpi-label">Direct referrals</div>
            <div className="kpi-num">{directRefs}</div>
            <div className="text-[11px] text-ink-3 mt-1">first-line wallets</div>
          </div>
          <div className="kpi-tile" data-testid="org-kpi-people">
            <div className="kpi-label">Wallets</div>
            <div className="kpi-num">{nodes.length}</div>
            <div className="text-[11px] text-ink-3 mt-1">community accounts</div>
          </div>
        </div>

        {/* ===== chart with toolbar (add root / download / zoom) ===== */}
        <div
          className={`glass overflow-hidden ${fullscreen ? "fixed inset-0 z-[140] flex flex-col !rounded-none" : ""}`}
          style={fullscreen ? { background: "#F4F5FB", backdropFilter: "none" } : undefined}
          data-testid="org-chart-section"
        >
          <div className="flex items-center justify-between gap-3 p-5 border-b border-black/5 flex-wrap">
            <button onClick={() => setChartOpen((o) => !o)} className="flex items-center gap-3 text-left" data-testid="org-chart-toggle">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,58,237,0.12)" }}><Network size={18} style={{ color: "#7C3AED" }} /></div>
              <div>
                <div className="kpi-label">Organization chart</div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Your team structure</h3>
              </div>
              <ChevronDown size={20} className={`text-ink-3 transition-transform ml-1 ${chartOpen ? "rotate-180" : ""}`} />
            </button>

            {chartOpen && (
              <div className="flex items-center gap-2 flex-wrap">
                {nodes.length > 0 && (
                  <div className="relative" ref={searchWrapRef} data-testid="org-search-wrap">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
                      onFocus={() => setSearchOpen(true)}
                      placeholder="Search name · wallet · email…"
                      data-testid="org-search-input"
                      className="input-glass !py-2 !pl-7 !pr-7 text-sm w-56 placeholder:text-ink-3"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => { setSearch(""); setSearchOpen(false); }}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink rounded-md p-0.5"
                        data-testid="org-search-clear"
                        title="Clear search"
                      >
                        <X size={13} />
                      </button>
                    )}
                    {search && searchOpen && (searchInfo.matches?.size ?? 0) > 0 && (
                      <div className="absolute top-full left-0 mt-1.5 w-80 max-h-72 overflow-y-auto rounded-xl border border-black/10 bg-white shadow-xl z-50" data-testid="org-search-dropdown">
                        {nodes.filter((n) => searchInfo.matches.has(n.id)).slice(0, 20).map((n) => (
                          <button
                            key={n.id}
                            type="button"
                            onClick={() => { focusNode(n.id); setSearchOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-indigo-50/60 border-b border-black/5 last:border-0 transition-colors"
                            data-testid={`org-search-option-${n.id}`}
                          >
                            <div className="text-xs font-semibold text-slate-900 truncate">{n.name || n.email || "Unnamed"}</div>
                            {n.wallet_name && <div className="text-[10px] font-mono text-slate-500 truncate">{n.wallet_name}</div>}
                          </button>
                        ))}
                        {(searchInfo.matches?.size ?? 0) > 20 && (
                          <div className="px-3 py-1.5 text-[10px] text-slate-400">+{searchInfo.matches.size - 20} more — keep typing to narrow down</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {isLive && isAdmin && (
                  <>
                  {!user?.is_demo && (
                    <button onClick={() => setHistoryOpen(true)} className="btn-secondary !py-2 !px-3.5 text-sm" title="Wallet connections, batch sync & full CaryPact history import" data-testid="org-history-btn"><History size={14} /> CaryPact history</button>
                  )}
                  {nodes.length === 0 && (
                    <button onClick={() => { setChartRootSync(true); setSyncNode({ id: null, name: "CaryPact Live", wallet_name: "" }); }} className="btn-primary !py-2 !px-3.5 text-sm" style={{ background: "#7C3AED" }} data-testid="org-sync-bootstrap-btn"><FolderSync size={15} /> Connect &amp; build my tree</button>
                  )}
                  </>
                )}
                {isAdmin && (
                  <button onClick={openSnapshots} className="btn-secondary !py-2 !px-3.5 text-sm" data-testid="org-snapshots-btn" title="Restore from an earlier snapshot (last 5 kept)">
                    <History size={15} /> Snapshots
                  </button>
                )}

                {nodes.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="btn-secondary !py-2 !px-3.5 text-sm" data-testid="org-download"><Download size={15} /> Download</button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={8} className="min-w-[240px] bg-white/95 backdrop-blur-xl border border-black/[0.06] p-1.5 shadow-2xl" style={{ borderRadius: 14 }}>
                      {isAdmin && (
                        <DropdownMenuItem onClick={downloadCsvRoundTrip} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="org-download-csv">
                          <FileSpreadsheet size={15} className="text-indigo-600" />
                          <div className="flex flex-col leading-tight">
                            <span>CSV (round-trip)</span>
                            <span className="text-[10px] text-ink-3">Edit &amp; re-upload · 13 cols</span>
                          </div>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={downloadExcelStructured} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="org-download-excel-structured">
                        <FileSpreadsheet size={15} className="text-ink-2" /> Excel — structured report
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={downloadExcel} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="org-download-excel">
                        <FileSpreadsheet size={15} className="text-emerald-600" />
                        <div className="flex flex-col leading-tight">
                          <span>Excel (.xlsx)</span>
                          <span className="text-[10px] text-ink-3">Same 13 cols + rollups · re-uploadable</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={downloadPDF} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="org-download-pdf">
                        <FileText size={15} className="text-rose-600" />
                        <div className="flex flex-col leading-tight">
                          <span>PDF (.pdf)</span>
                          <span className="text-[10px] text-ink-3">Print / share only · not editable</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={downloadPoster} disabled={posterBusy} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="org-download-poster">
                        <Printer size={15} className="text-purple-600" />
                        <div className="flex flex-col leading-tight">
                          <span>{posterBusy ? "Building poster…" : "Poster PDF · 36×48″"}</span>
                          <span className="text-[10px] text-ink-3">High-res print · whole tree, fully expanded</span>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {nodes.length > 0 && (
                  <button
                    onClick={downloadPoster}
                    disabled={posterBusy}
                    className="btn-secondary !py-2 !px-3.5 text-sm"
                    data-testid="org-poster-btn"
                    title="High-resolution 36×48″ print poster of the whole tree"
                  >
                    <Printer size={15} className={posterBusy ? "animate-pulse" : ""} /> {posterBusy ? "Building…" : "Download poster"}
                  </button>
                )}

                {nodes.length > 0 && (
                  <button
                    onClick={doRefresh}
                    disabled={refreshing}
                    className="btn-secondary !py-2 !px-3.5 text-sm"
                    data-testid="org-refresh-btn"
                    title="Reload the chart from the server"
                  >
                    <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
                  </button>
                )}

                {nodes.length > 0 && (
                  <div className="flex gap-1 p-1 rounded-xl" style={{ background: "rgba(15,18,38,0.04)" }} data-testid="org-view-toggle">
                    <button
                      type="button"
                      onClick={() => setMode("simple")}
                      className={`py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${viewMode === "simple" ? "text-white" : "text-ink-2 hover:bg-white/60"}`}
                      style={viewMode === "simple" ? { background: "linear-gradient(135deg, #7C3AED, #A78BFA)" } : {}}
                      title="Names + tree only"
                      data-testid="org-view-simple"
                    >
                      Simple
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode("detailed")}
                      className={`py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${viewMode === "detailed" ? "text-white" : "text-ink-2 hover:bg-white/60"}`}
                      style={viewMode === "detailed" ? { background: "linear-gradient(135deg, #4060FF, #6B8AFF)" } : {}}
                      title="Full tiles — money, status, actions"
                      data-testid="org-view-detailed"
                    >
                      Detailed
                    </button>
                  </div>
                )}

                {nodes.length > 0 && (
                  <button onClick={() => setFullscreen((f) => !f)} className="btn-secondary !py-2 !px-3.5 text-sm" data-testid="org-fullscreen-toggle" title={fullscreen ? "Exit full screen (Esc)" : "Full screen — show only the chart"}>
                    {fullscreen ? <><Shrink size={15} /> Exit full screen</> : <><Expand size={15} /> Full screen</>}
                  </button>
                )}

                {nodes.length > 0 && (
                  <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: "rgba(15,18,38,0.04)" }} data-testid="org-zoom-controls">
                    <button onClick={zoomOut} className={zoomBtn} title="Zoom out" data-testid="org-zoom-out"><Minus size={15} /></button>
                    <span className="text-xs num w-11 text-center text-ink-2 font-medium" data-testid="org-zoom-level">{Math.round(zoom * 100)}%</span>
                    <button onClick={zoomIn} className={zoomBtn} title="Zoom in" data-testid="org-zoom-in"><Plus size={15} /></button>
                    <div className="w-px h-5 mx-1" style={{ background: "rgba(15,18,38,0.10)" }} />
                    <button onClick={fit} className={zoomBtn} title="Fit to screen" data-testid="org-zoom-fit"><Maximize2 size={14} /></button>
                    <button onClick={reset} className={zoomBtn} title="Reset" data-testid="org-zoom-reset"><RotateCcw size={14} /></button>
                    <div className="w-px h-5 mx-1" style={{ background: "rgba(15,18,38,0.10)" }} />
                    <button onClick={collapseAll} className={zoomBtn} title="Collapse all branches" data-testid="org-collapse-all"><ChevronsDownUp size={15} /></button>
                    <button onClick={expandAll} className={zoomBtn} title="Expand all branches" data-testid="org-expand-all"><ChevronsUpDown size={15} /></button>
                  </div>
                )}
              </div>
            )}
          </div>

          {chartOpen && (
            loading ? (
              <div className="text-sm text-ink-3 py-24 text-center" data-testid="org-chart-loading">Loading…</div>
            ) : nodes.length === 0 ? (
              <div className="py-24 text-center" data-testid="org-chart-empty">
                {isAdmin ? (
                  <>
                    <p className="text-sm text-ink-3 mb-4">No wallets yet — connect your TokenPocket wallet and run the first sync. Every tile is built from CaryPact, nothing is added by hand.</p>
                    <button onClick={() => { setChartRootSync(true); setSyncNode({ id: null, name: "CaryPact Live", wallet_name: "" }); }} className="btn-primary" style={{ background: "#7C3AED" }} data-testid="org-sync-first"><FolderSync size={15} /> Connect &amp; build my tree</button>
                  </>
                ) : (
                  <p className="text-sm text-ink-3">You're not linked to a team yet. Ask your sponsor to give you access from their org chart.</p>
                )}
              </div>
            ) : (
              <div
                ref={viewportRef}
                className="org-viewport relative"
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                style={{ height: fullscreen ? "auto" : "66vh", flex: fullscreen ? "1 1 auto" : undefined, minHeight: fullscreen ? 0 : 480, overflow: "hidden", cursor: dragging ? "grabbing" : "grab", userSelect: "none" }}
                data-testid="org-viewport"
              >
                <div className="absolute left-2 bottom-2 z-10 flex items-center gap-1.5 text-[11px] text-ink-3 px-2.5 py-1.5 rounded-lg" style={{ background: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)" }}>
                  <Move size={12} /> Drag to pan · scroll to zoom · <b className="text-ink-2">click a tile to focus</b>
                </div>
                <div
                  ref={treeRef}
                  style={{ position: "absolute", left: "50%", top: 28, transform: `translate(calc(-50% + ${pan.x}px), ${pan.y}px) scale(${zoom})`, transformOrigin: "top center", transition: dragging ? "none" : "transform .12s ease" }}
                >
                  <div className="org-tree" data-testid="org-tree">
                    <ul>{roots.map((r) => renderNode(r, 0))}</ul>
                  </div>
                </div>
              </div>
            )
          )}
        </div>

        {error && !modalOpen && <div className="mt-4 chip chip-amber" data-testid="org-error">{error}</div>}
      </div>

      {/* ===== add / edit modal ===== */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="org-modal">
          <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.40)", backdropFilter: "blur(4px)" }} onClick={closeModal} />
          <div className="relative glass w-full max-w-md p-6 max-h-[92vh] overflow-y-auto" style={{ borderRadius: 20 }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="kpi-label">{editingId ? "Edit wallet" : (form.parent_id ? `Add wallet under ${parentName || "manager"}` : "Add root wallet")}</div>
                <h3 className="font-display text-lg font-semibold tracking-tight">{editingId ? "Update details" : "New team member"}</h3>
              </div>
              <button onClick={closeModal} className="org-icon-btn" title="Close" data-testid="org-modal-close"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="org-form">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Person name *" className="input-glass w-full" data-testid="org-name" autoFocus />
              <select required={!isAdmin} value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="input-glass w-full" data-testid="org-parent">
                {isAdmin
                  ? <option value="">Reports to… (top level)</option>
                  : <option value="" disabled>Reports to… (pick someone in your branch) *</option>}
                {nodes.filter((n) => !blocked.has(n.id)).map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.wallet_name} onChange={(e) => setForm({ ...form, wallet_name: e.target.value })} placeholder="Wallet name" className="input-glass w-full" data-testid="org-wallet" />
                <select
                  value={form.rank}
                  onChange={(e) => setForm({ ...form, rank: e.target.value })}
                  className="input-glass w-full"
                  data-testid="org-rank"
                  title="CaryPact V-tier rank"
                >
                  <option value="">Rank (V-tier) …</option>
                  {RANK_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="Principal (USDT)"
                  className="input-glass w-full"
                  data-testid="org-amount"
                  title="Starting stake / investment amount in USDT"
                />
                <input
                  type="number"
                  step="0.01"
                  value={form.current_value}
                  onChange={(e) => setForm({ ...form, current_value: e.target.value })}
                  placeholder="Current value (USDT)"
                  className="input-glass w-full"
                  data-testid="org-current-value"
                  title="Current portfolio value — leave blank if same as principal"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="input-glass w-full" data-testid="org-email" />
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-glass w-full" data-testid="org-status">
                  <option value="">Status…</option>
                  {["Lead", "Onboarded", "Active", "Paused", "Inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select
                  required
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value, state: "" })}
                  className="input-glass w-full"
                  data-testid="org-country"
                >
                  <option value="" disabled>Country *</option>
                  {form.country && !COUNTRIES.includes(form.country) && (
                    <option value={form.country}>{form.country} (existing)</option>
                  )}
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                  required
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="input-glass w-full disabled:opacity-50"
                  data-testid="org-state"
                  disabled={!form.country}
                >
                  <option value="" disabled>
                    {form.country ? "State / Province *" : "Pick country first"}
                  </option>
                  {form.country && form.state && !getStatesFor(form.country).includes(form.state) && (
                    <option value={form.state}>{form.state} (existing)</option>
                  )}
                  {getStatesFor(form.country).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={form.wallet_kind}
                  onChange={(e) => setForm({ ...form, wallet_kind: e.target.value })}
                  className="input-glass w-full"
                  data-testid="org-wallet-kind"
                >
                  <option value="">Wallet for…</option>
                  <option value="Mining">Mining</option>
                  <option value="Staking">Staking</option>
                  <option value="Both">Both (Mining + Staking)</option>
                </select>
                <input
                  type="date"
                  value={form.forecast_date}
                  onChange={(e) => setForm({ ...form, forecast_date: e.target.value })}
                  className="input-glass w-full"
                  data-testid="org-forecast-date"
                  title="Projected break-even / recovery date"
                />
              </div>
              <div className="text-[11px] text-ink-3 -mt-1">Email is required — members get a 6-digit sign-in code on each new browser, and invite links are sent here. Country &amp; state are required for compliance and tax-jurisdiction tagging. Forecast date is the projected break-even target.</div>
              <input value={form.invite_link} onChange={(e) => setForm({ ...form, invite_link: e.target.value })} placeholder="CaryPact invitation link" className="input-glass w-full" data-testid="org-invite" />
              <input value={form.affiliate_name} onChange={(e) => setForm({ ...form, affiliate_name: e.target.value })} placeholder='Affiliate name — e.g. "Stuart R."' className="input-glass w-full" data-testid="org-affiliate-name" />
              <div className="text-[11px] text-ink-3 -mt-1">Affiliate name is the public-facing name shown as &quot;Invited by&quot; on this person&apos;s share links (join &amp; discover pages).</div>
              {error && <div className="chip chip-amber" data-testid="org-modal-error">{error}</div>}
              <div className="flex gap-2 pt-1">
                <button type="submit" className="btn-primary flex-1" data-testid="org-submit"><Plus size={14} /> {editingId ? "Save changes" : "Add person"}</button>
                <button type="button" onClick={closeModal} className="btn-secondary" data-testid="org-cancel">Cancel</button>
              </div>
            </form>

            {/* ===== login access (edit mode only) ===== */}
            {editingId && (
              <div className="mt-5 pt-5 border-t border-black/5" data-testid="org-login-panel">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(64,96,255,0.12)" }}><KeyRound size={14} style={{ color: "#4060FF" }} /></div>
                  <div>
                    <div className="kpi-label">Team login</div>
                    <h4 className="font-display text-sm font-semibold tracking-tight">Account access for this tile</h4>
                  </div>
                </div>

                {acctStatus === "active" ? (
                  <>
                    <div className="chip chip-green inline-flex items-center gap-1.5" data-testid="org-login-active"><ShieldCheck size={13} /> Active account · {editingNode?.account_role === "affiliate_manager" ? "Leader" : "Member"} · {editingNode?.email}</div>
                    {grant.error && <div className="chip chip-amber mt-2" data-testid="org-grant-error">{grant.error}</div>}
                    <button type="button" onClick={doRevoke} disabled={revoking} className="btn-secondary w-full justify-center text-sm mt-3" style={{ color: "#DC2626" }} data-testid="org-revoke-login">
                      <ShieldOff size={14} /> {revoking ? "Unlinking…" : "Unlink from this tile"}
                    </button>
                    <p className="text-[11px] text-ink-3 mt-1.5">Clears this tile's link — the account stays. They can still log in and be linked to a different tile. To remove the account itself, go to Members → Archive.</p>
                  </>
                ) : (
                  <>
                    {acctStatus === "invited" && (
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <div className="chip chip-amber inline-flex items-center gap-1.5" data-testid="org-login-invited">Invited · awaiting password · {editingNode?.email}</div>
                        <button type="button" onClick={doRevoke} disabled={revoking} className="text-xs font-medium underline underline-offset-2" style={{ color: "#DC2626" }} title="Cancel this invite & remove the pending account" data-testid="org-revoke-invite">
                          {revoking ? "Removing…" : "Cancel invite"}
                        </button>
                      </div>
                    )}
                    {acctStatus === "none" && isAdmin && (
                      <div className="mb-3 rounded-xl p-3" style={{ background: "rgba(0,134,58,0.05)", border: "1px solid rgba(0,134,58,0.18)" }} data-testid="org-link-existing">
                        <div className="text-[11px] text-ink-2 mb-2 leading-relaxed">
                          <b>Have an account already?</b> Link this tile to an existing user&apos;s email (e.g. your own admin account or a member who signed up elsewhere). Their revenue rolls up into the chart.
                        </div>
                        <div className="flex gap-2">
                          <input type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="existing@account.com" className="input-glass flex-1 text-sm" data-testid="org-link-email" />
                          <button type="button" onClick={doLink} disabled={linking || !linkEmail} className="btn-secondary text-sm" data-testid="org-link-submit">
                            <Link2 size={13} /> {linking ? "Linking…" : "Link"}
                          </button>
                        </div>
                        {linkError && <div className="chip chip-amber mt-2 text-xs" data-testid="org-link-error">{linkError}</div>}
                      </div>
                    )}
                    <div className="rounded-xl p-3 text-[12px] text-ink-2 leading-relaxed" style={{ background: "rgba(64,96,255,0.05)", border: "1px solid rgba(64,96,255,0.15)" }} data-testid="org-academy-note">
                      <b>New members join through CP Academy.</b> Share your funnel link (Academy page) — they complete the 12 lessons and the graduation form, then you provision their login from <b>Admin → Academy</b>. They appear on this chart automatically. Direct login grants are retired so nobody skips training.
                    </div>
                    {grant.error && <div className="chip chip-amber mt-2" data-testid="org-grant-error">{grant.error}</div>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ===== CSV upload modal (admin-only) ===== */}
      {uploadOpen && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="org-upload-modal">
          <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.40)", backdropFilter: "blur(4px)" }} onClick={closeUpload} />
          <div className="relative glass w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" style={{ borderRadius: 20 }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="kpi-label">Bulk upload · CSV</div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Import wallets in one shot</h3>
              </div>
              <button onClick={closeUpload} className="org-icon-btn" title="Close" data-testid="org-upload-close"><X size={16} /></button>
            </div>

            <div className="space-y-3">
              <div className="text-sm text-ink-2 leading-relaxed">
                Upload a <b>CSV or Excel</b> file with these columns
                <span className="text-ink-3"> (same 13 as the Download menu — round-trip safe)</span>:
              </div>
              <code
                className="block text-[11px] font-mono px-3 py-2 rounded-lg overflow-x-auto whitespace-nowrap"
                style={{ background: "rgba(255,255,255,0.55)", border: "1px solid rgba(15,18,38,0.08)" }}
                data-testid="org-upload-header"
              >
                label, wallet, account_email, parent_wallet, country, state, wallet_type, forecast_date, status, rank, principal, current_value, carry_pact_link
              </code>
              <ul className="text-[11px] text-ink-3 list-disc pl-5 space-y-1">
                <li><b>label</b>, <b>account_email</b>, <b>country</b>, <b>state</b> are required per row.</li>
                <li><b>parent_wallet</b> — leave blank for top-level, or match an existing wallet name / person name.</li>
                <li><b>wallet_type</b> = Mining / Staking / Both · <b>forecast_date</b> = YYYY-MM-DD · <b>rank</b> = V1..V11.</li>
                <li><b>principal</b> = starting USDT · <b>current_value</b> = current portfolio USDT (leave blank if same).</li>
                <li>Duplicates (same wallet name OR same account_email) are <b>skipped</b> and reported.</li>
                <li>A rolling snapshot is taken automatically before every upload — restorable via <b>Snapshots</b>.</li>
              </ul>

              <button type="button" onClick={downloadCsvTemplate} className="btn-secondary text-sm" data-testid="org-upload-template">
                <Download size={14} /> Download CSV template
              </button>

              <div className="pt-3 border-t border-black/5">
                <label className="block text-xs text-ink-2 mb-2 font-medium">Pick your file (.csv or .xlsx)</label>
                <input
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadResult(null); setUploadError(""); }}
                  className="block w-full text-sm text-ink-2 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-white/70 file:text-ink-1 hover:file:bg-white"
                  data-testid="org-upload-file"
                />
                {uploadFile && (
                  <div className="text-[11px] text-ink-3 mt-1.5" data-testid="org-upload-filename">
                    Selected: {uploadFile.name} · {(uploadFile.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>

              {/* Duplicate-handling mode toggle */}
              <div className="pt-3">
                <label className="block text-xs text-ink-2 mb-2 font-medium">
                  If a wallet or email already exists…
                </label>
                <div className="flex gap-1 p-1 rounded-xl w-full" style={{ background: "rgba(15,18,38,0.05)" }} data-testid="org-upload-mode">
                  <button
                    type="button"
                    onClick={() => setUploadMode("skip")}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${uploadMode === "skip" ? "text-white" : "text-ink-2 hover:bg-white/50"}`}
                    style={uploadMode === "skip" ? { background: "linear-gradient(135deg, #4060FF, #6B8AFF)" } : {}}
                    data-testid="org-upload-mode-skip"
                  >
                    Skip duplicates
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode("update")}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${uploadMode === "update" ? "text-white" : "text-ink-2 hover:bg-white/50"}`}
                    style={uploadMode === "update" ? { background: "linear-gradient(135deg, #7C3AED, #A78BFA)" } : {}}
                    data-testid="org-upload-mode-update"
                  >
                    Update duplicates
                  </button>
                </div>
                <p className="text-[11px] text-ink-3 mt-1.5 leading-relaxed" data-testid="org-upload-mode-hint">
                  {uploadMode === "skip"
                    ? "Existing tiles stay untouched · only new rows create tiles. Use this to add wallets safely."
                    : "Existing tiles are overwritten with the CSV row (rank, principal, current_value, status, forecast_date, wallet_type, parent). Perfect for the round-trip download → edit → upload flow."}
                </p>
              </div>

              {uploadError && (
                <div className="chip chip-amber inline-flex items-center gap-1.5" data-testid="org-upload-error">
                  <AlertCircle size={12} /> {uploadError}
                </div>
              )}

              {uploadResult && (
                <div className="rounded-xl p-3.5 text-sm" style={{ background: "rgba(64,96,255,0.06)", border: "1px solid rgba(64,96,255,0.16)" }} data-testid="org-upload-result">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="chip chip-green" data-testid="org-upload-created">Created {uploadResult.created}</span>
                    {uploadResult.updated > 0 && (
                      <span className="chip chip-blue" data-testid="org-upload-updated">Updated {uploadResult.updated}</span>
                    )}
                    <span className="chip chip-gray" data-testid="org-upload-skipped">Skipped {uploadResult.skipped_duplicates}</span>
                    <span className="chip chip-amber" data-testid="org-upload-errors">Errors {uploadResult.errors?.length || 0}</span>
                    {uploadResult.warnings?.length > 0 && (
                      <span className="chip chip-pink" data-testid="org-upload-warnings">Warnings {uploadResult.warnings.length}</span>
                    )}
                    {uploadResult.mode && (
                      <span className="text-[11px] font-mono text-ink-3 ml-auto" data-testid="org-upload-mode-echo">
                        mode: {uploadResult.mode}
                      </span>
                    )}
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto text-[11px] font-mono text-ink-2 space-y-1 mt-2" data-testid="org-upload-error-list">
                      <div className="text-ink-3 font-medium">Errors (row skipped):</div>
                      {uploadResult.errors.map((e, i) => (
                        <div key={i} data-testid={`org-upload-error-${i}`}>Row {e.row}: {e.reason}</div>
                      ))}
                    </div>
                  )}
                  {uploadResult.warnings?.length > 0 && (
                    <div className="max-h-32 overflow-y-auto text-[11px] font-mono text-ink-2 space-y-1 mt-2" data-testid="org-upload-warning-list">
                      <div className="text-ink-3 font-medium">Warnings (row still added):</div>
                      {uploadResult.warnings.map((w, i) => (
                        <div key={i} data-testid={`org-upload-warning-${i}`}>Row {w.row}: {w.reason}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={submitUpload}
                  disabled={!uploadFile || uploading}
                  className="btn-primary flex-1"
                  data-testid="org-upload-submit"
                >
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
                </button>
                <button type="button" onClick={closeUpload} className="btn-secondary" data-testid="org-upload-cancel">
                  {uploadResult ? "Done" : "Cancel"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ===== Snapshots / Restore modal (admin-only) ===== */}
      {snapshotsOpen && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="org-snapshots-modal">
          <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.40)", backdropFilter: "blur(4px)" }} onClick={closeSnapshots} />
          <div className="relative glass w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" style={{ borderRadius: 20 }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="kpi-label">Rolling backups · last 5</div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Restore your org chart</h3>
              </div>
              <button onClick={closeSnapshots} className="org-icon-btn" title="Close" data-testid="org-snapshots-close"><X size={16} /></button>
            </div>

            <p className="text-[12px] text-ink-3 leading-relaxed mb-4">
              A snapshot is taken automatically before every CSV upload. The five most recent are kept; older ones roll off.
              Restoring will <b>replace</b> every current tile with the frozen copy — but a fresh snapshot of the current state is taken first, so a bad restore can itself be rolled back.
            </p>

            {snapshots.length === 0 ? (
              <div className="text-sm text-ink-3 py-8 text-center" data-testid="org-snapshots-empty">
                No snapshots yet. One will be created automatically the next time you upload a CSV.
              </div>
            ) : (
              <div className="space-y-2" data-testid="org-snapshots-list">
                {snapshots.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl"
                    style={{ background: "rgba(255,255,255,0.55)", border: "1px solid rgba(15,18,38,0.08)" }}
                    data-testid={`org-snapshot-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink-1 truncate">
                        {new Date(s.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                      <div className="text-[11px] text-ink-3 truncate" title={s.reason}>{s.reason || "Manual snapshot"}</div>
                    </div>
                    <div className="chip chip-gray shrink-0" data-testid={`org-snapshot-count-${s.id}`}>{s.count} tiles</div>
                    <button
                      onClick={() => doRestore(s)}
                      disabled={restoringId === s.id}
                      className="btn-secondary text-sm !py-1.5 !px-2.5 shrink-0"
                      data-testid={`org-snapshot-restore-${s.id}`}
                    >
                      <RotateCcw size={13} /> {restoringId === s.id ? "Restoring…" : "Restore"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}



      {historyOpen && (
        <CaryPactHistoryModal onClose={() => setHistoryOpen(false)} onImported={() => fetchNodes()} />
      )}

      {syncNode && (
        <CaryPactLiveSyncModal
          node={syncNode}
          chartRoot={chartRootSync}
          onClose={() => { setSyncNode(null); setChartRootSync(false); }}
          onSynced={() => { fetchNodes(); }}
        />
      )}

      <HelpFab />
    </div>
  );
}
